/* Desktop + in-app notifications for incoming Direct Messages.
 *
 * Listens to the global SSE `atelier:chat_message` event and shows a
 * discrete notification whenever a DM arrives that the current user is
 * NOT actively looking at. Own messages are always ignored.
 *
 * Mounted once at the AppLayout level so notifications keep firing even
 * when the user is on any other page.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "./auth";
import { log } from "./log";

// The ChatPage writes its currently-open channel id here so this notifier
// can skip notifications the user is already looking at. Keeping the state
// on `window` avoids threading a context just for a small optimisation.
export function setActiveChatChannel(cid) {
  try { window.__activeChatChannelId = cid || null; } catch { /* SSR safe */ }
}

function isViewingChannel(cid) {
  try {
    const onChatRoute = window.location.pathname === "/chat";
    const active = window.__activeChatChannelId;
    return onChatRoute && !document.hidden && active === cid;
  } catch { return false; }
}

function ensurePermission() {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    Notification.requestPermission().catch(() => {});
  }
}

export function useDmNotifier() {
  const { user } = useAuth();
  const permissionAskedRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    // Ask for permission on the first user gesture — Chrome ignores the
    // request when it's fired outside of user interaction, so we hook
    // into a single "pointerdown".
    const askOnce = () => {
      if (permissionAskedRef.current) return;
      permissionAskedRef.current = true;
      ensurePermission();
      window.removeEventListener("pointerdown", askOnce);
    };
    window.addEventListener("pointerdown", askOnce, { once: true });

    const onMsg = (e) => {
      const { channel_id, channel_type, channel_name, message } = e.detail || {};
      if (channel_type !== "dm") return;
      if (!message || message.sender_id === user.id) return;   // never notify for own messages
      if (isViewingChannel(channel_id)) return;                // already reading it

      const from = message.sender_name || message.sender_email || "Team member";
      const body = (message.text || "").slice(0, 140) || "(allegato)";

      // In-app toast — always shown, works without OS permission.
      toast(from, {
        description: body,
        duration: 6000,
        action: {
          label: "Apri",
          onClick: () => {
            window.location.href = "/chat";
          },
        },
      });

      // OS-level notification when the tab is hidden or focus is elsewhere.
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const n = new Notification(`Messaggio da ${from}`, {
            body,
            tag: `dm-${channel_id}`,   // collapse repeated notifications from the same DM
            icon: "/favicon.png",
            silent: false,
          });
          n.onclick = () => {
            try {
              window.focus();
              window.location.href = "/chat";
              n.close();
            } catch (err) {
              log.warn("[dm-notify] click handler failed", err);
            }
          };
        }
      } catch (err) {
        log.warn("[dm-notify] Notification API failed", err);
      }
    };

    window.addEventListener("atelier:chat_message", onMsg);
    return () => {
      window.removeEventListener("atelier:chat_message", onMsg);
      window.removeEventListener("pointerdown", askOnce);
    };
  }, [user]);
}
